import React, { useEffect, useState } from 'react';
import { ReactComponent as PartsNotFoundImage } from 'assets/images/parts-not-found.svg';
import Pagination from 'components/pagination/Pagination';
import Spinner from 'components/spinner/Spinner';
import { useTranslation } from 'react-i18next';
import useHttp from 'utils/hooks/useHttp';
import { useSearchParams } from 'react-router-dom';
import ConsumableNavbar from 'components/navbar/consumable-navbar/ConsumableNavbar';
import { IBodyResponseConsumable, IConsumableCatalogFilter } from 'interfaces/consumable';
import ProductsViewMode from 'components/ProductsViewMode/ProductsViewMode';
import { DEFAULT_VIEW } from 'components/ProductsViewMode/ProductsViewMode.constants';
import ProductsViewModeChanger from 'components/ProductsViewModeChanger/ProductsViewModeChanger';
import { CATALOG_CONSUMABLE } from 'components/ProductsViewModeChanger/products-view-mode-changer-utils';
import List from 'components/List/List';
import { ICategory } from 'views/Sidebar/Sidebar.model';
import CatalogConsumableFilter from '../CatalogConsumableFilter/CatalogConsumableFilter';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { sortByOptions } from 'components/SortBy/SortBy.map';
import SortBy, { SortByParams } from 'components/SortBy/SortBy';
import './CatalogConsumablesMobile.scss';
import useReliableCatalogCarQueryParams from 'utils/hooks/useReliableCatalogCarQueryParams';

const CatalogConsumablesMobile: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const { t } = useTranslation();
  const http = useHttp();
  const [loading, setLoading] = useState(true);
  const [refData, setRefData] = useState<IBodyResponseConsumable>();
  const [filterData, setFilterData] = useState<IConsumableCatalogFilter>();
  const [openFilter, setOpenFilter] = useState(false);
  const [consumableCategories, setConsumableCategories] = useState<ICategory[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [isCategorySelected, setIsCategorySelected] = useState(false);
  const [page, setPage] = useState(Number(searchParams.get('page')));
  const user = JSON.parse(localStorage.getItem('userData')!);
  const [viewMode, setViewMode] = useState(user?.user_settings.products_view_mode || DEFAULT_VIEW);
  const [activeCategoryInfo, setActiveCategoryInfo] = useState<ICategory>();
  const [totalCount, setTotalCount] = useState(0);
  const reliableSearchParams = useReliableCatalogCarQueryParams();

  function getConsumableCategories() {
    setLoadingCategories(true);
    http.catalogs.getCarConsumableAllCategories({}).then((res) => {
      setConsumableCategories(res);
      setTotalCount(res.reduce((total, item) => total + Number(item.number_of_products), 0));
    }).finally(() => setLoadingCategories(false));
  }

  function getConsumableData(data) {
    setLoading(true);
    setRefData(undefined);
    const sortBySelectedOption = sortByOptions.filter((item) => String(item.value) == String(searchParams.get('sort')))[0] ?? [];

    const sortParams: SortByParams = {
      sort_by_column: String(searchParams.get('sort') ?? ''),
      sort_by_asc: String(searchParams.get('asc') ?? sortBySelectedOption?.default_sort_asc ?? ''),
    };

    const params = {
      ...data,
      ...sortParams,
      page,
    };

    const validParams = validateParams(params);

    http.consumableCatalogHttp
      .getConsumables(validParams)
      .then((res) => {
        setRefData(res.data);
      })
      .finally(() => setLoading(false));
  }

  function getConsumableFilter(data) {
    const validFilterParams = validateParams(data);

    http.consumableCatalogHttp.getConsumableFilters(validFilterParams).then((res) => {
      setFilterData(res.data);
    });
  }

  const validateParams = (data) => {
    return Object.entries(data).reduce((acc, [key, value]) => {
      if (!value) {
        return acc;
      }

      acc[key] = value;

      return acc;
    }, {});
  };


  const handlePageClick = (pageN: number) => {
    setPage(pageN);
  };

  const onCategoryClick = (category) => {
    setActiveCategoryInfo(category);
    setIsCategorySelected(true);
    setSearchParams({ category_id: [category.id] });
    setPage(1);
  };

  useEffect(() => {
    const params = {
      q: searchParams.get('q'),
      category_ids: searchParams.getAll('category_id'),
      brand_ids: searchParams.getAll('brand_id'),
    };

    if (searchParams.get('category_id')) {
      getConsumableData(params);
    }
  }, [
    page,
    searchParams.get('category_id'),
    searchParams.get('brand_id'),
    searchParams.get('sort'),
    searchParams.get('asc'),
  ]);


  useEffect(() => {
    const params = {
      q: searchParams.get('q'),
      category_ids: searchParams.getAll('category_id'),
      brand_ids: searchParams.getAll('brand_id'),
    };

    if (searchParams.get('category_id')) {
      getConsumableFilter(params);
    }
  }, [
    searchParams.get('category_id'),
  ]);
  useEffect(() => {
    getConsumableCategories();
  }, []);


  function filterOptionsElement() {
    if (!searchParams.get('category_id')) {
      return <div className="catalog-parts-filter"></div>;
    }

    return (
      <div className="catalog-parts-filter">
        <button onClick={() => setOpenFilter(true)} className={`catalog-parts-button ${openFilter ? 'active' : ''}`}>
          {t('_filter_results_btn')}
        </button>
      </div>
    );
  }


  const onClickReturn = () => {

    setSearchParams({ ...reliableSearchParams });
    setIsCategorySelected(false);
  };

  return (
    <div className="consumables-wrapper-mobile">
      <ConsumableNavbar option={filterOptionsElement()} title="_consumable_catalog" />
      <div className="separator"></div>

      <div className="filterOptions">
        {refData?.data?.length && refData?.data?.length > 0 && searchParams.get('category_id') ? <SortBy /> : null}
        <div className="results">
          {!isCategorySelected && totalCount ? totalCount + ' ' + t('_results') : (refData?.data?.length && refData?.data?.length > 0 ? refData?.total_count + ' ' + t('_results') : (loading ? <Spinner class="extra-small" /> : null))}
        </div>
        <ProductsViewMode viewMode={viewMode} setViewMode={setViewMode} />
      </div>

      {isCategorySelected ? (
        <div className="consumables-catalog-heading">
          <div className="consumables-category-return" onClick={() => onClickReturn()}><FontAwesomeIcon icon={faCircleArrowLeft} />{t('_return')}</div>
          <div className="consumables-category-title">{activeCategoryInfo?.name}</div>
        </div>
      ) : null}

      <div className="retailer-catalog-consumables-container-mobile">
        {!isCategorySelected ? (
          <div className={`catalog-consumables-sidebar-mobile ${loadingCategories ? 'loading' : null}`}>
            {loadingCategories ? <Spinner class="small" /> : (
              consumableCategories && consumableCategories.length ? (
                <List
                  content={consumableCategories?.map((item) => {
                    return { ...item, id: item.id, name: item.name };
                  })}
                  images={consumableCategories?.map((item) => item.image)}
                  itemClassName="parts-list-catalog-item"
                  itemId="id"
                  onItemClick={onCategoryClick}
                />
              ) : null
            )}
          </div>
        ) : (
          <div className="wrapperItems">
            {loading ? (
              <Spinner class="car-parts-spinner" />
            ) : refData?.data?.length ? (
              <ProductsViewModeChanger catalog={CATALOG_CONSUMABLE} viewMode={viewMode} data={refData?.data} />
            ) : (
              <div className="no-parts-data">
                <div className="notFoundSvg">
                  <PartsNotFoundImage />
                </div>
                <h1>{t('_consumables_not_found')}</h1>
              </div>
            )}
          </div>
        )}
      </div>
      {(refData && refData?.total_pages && refData?.page && refData?.total_pages > 1) && isCategorySelected ? (
        <Pagination pageCount={refData.total_pages} handlePageClick={handlePageClick} forcePage={refData.page} />
      ) : (
        null
      )}
      <CatalogConsumableFilter getData={getConsumableData} filterData={filterData} openFilter={openFilter} setOpenFilter={setOpenFilter} />
    </div>
  );
};

export default CatalogConsumablesMobile;
